import CloseIcon from "@mui/icons-material/Close";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import ImportContactsIcon from "@mui/icons-material/ImportContacts";
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from "@mui/material";

import sheetsPreview from "@/assets/noAvailablepreview/sheets.png";
import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import useToast from "@/contexts/toast-context";
import { formatBytes } from "@/helpers/file-helpers";
import useActivePlans from "@/hooks/use-active-plans";
import useImportContacts from "@/hooks/use-import-contacts";
import { ContactsMessages } from "@/messages/contacts/contacts.messages";
import contactService from "@/services/contact.service";

const contactsSampleFileUrl = `${import.meta.env.VITE_API_URL}/examples/contatos.xlsx`;

interface ContactImportModalProps {
  onClose: () => void;
  open: boolean;
  messages: ContactsMessages;
}

export function ContactsImportModal({ open, onClose, messages }: ContactImportModalProps) {
  const { getRootProps, getInputProps, uploadedFile, removeFile } = useImportContacts({ messages });
  const { hasActivePlans, isLoading: isLoadingPlans } = useActivePlans();
  const canDownloadSampleFile = hasActivePlans && !isLoadingPlans;
  const handleClose = () => {
    onClose();
    removeFile();
  };
  const { showToast } = useToast();

  const handleDownloadContactssSampleFile = async () => {
    if (!canDownloadSampleFile) {
      return;
    }

    const link = document.createElement("a");

    link.href = contactsSampleFileUrl;
    link.download = "contatos.xlsx";
    link.target = "_blank";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImportContacts = async (file: File | null) => {
    if (!file) {
      alert("Please upload a file before importing.");
      return;
    }

    try {
      const success = await contactService.importContactsFromFile(file);
      if (!success) {
        showToast({ text: messages.importContactsError, type: "error" });
        return;
      }
      showToast({ text: messages.importContactsSuccess, type: "success" });

      handleClose();
    } catch (error) {
      showToast({ text: messages.importContactsError, type: "error" });
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>{messages.importContactsTitle}</DialogTitle>
      <DialogContent dividers>
        <MDBox p={3}>
          <IconButton
            onClick={handleClose}
            sx={(theme) => ({
              position: "absolute",
              top: theme.functions.pxToRem(12),
              right: theme.functions.pxToRem(12),
            })}
          >
            <CloseIcon />
          </IconButton>
          <MDBox p={2} sx={{ border: "2px dashed " }} {...getRootProps()}>
            <input {...getInputProps()} />
            <MDBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
            >
              {uploadedFile ? (
                <>
                  <img src={sheetsPreview} alt="Text Preview" width={88} height={110} />

                  <MDTypography variant="body2" textAlign="center">
                    {uploadedFile.name} - ({formatBytes(uploadedFile.size)})
                  </MDTypography>
                </>
              ) : (
                <>
                  <FileUploadIcon sx={{ width: 25, height: 25 }} />
                  <MDTypography variant="body2" textAlign="center">
                    {messages.importContactsPlaceholder}
                  </MDTypography>
                </>
              )}
            </MDBox>
          </MDBox>
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton
          variant="gradient"
          color="light"
          disabled={!canDownloadSampleFile}
          onClick={handleDownloadContactssSampleFile}
          startIcon={<ImportContactsIcon />}
        >
          {messages.downloadImportContactsSampleFile}
        </MDButton>
        <MDButton
          variant="gradient"
          color="dark"
          disabled={!uploadedFile}
          onClick={() => handleImportContacts(uploadedFile)}
        >
          {messages.importContactsButton}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
